import { Controller } from "@hotwired/stimulus"

// Contrôleur pour gérer la compatibilité avec Trusted Types
export default class extends Controller {
    static targets = []

    connect() {
        // Créer une politique Trusted Types si elle n'existe pas déjà
        this.setupTrustedTypesPolicy()
    }

    setupTrustedTypesPolicy() {
        // Vérifier si Trusted Types sont supportés et requis
        if (typeof window.trustedTypes !== 'undefined') {
            try {
                // Créer une politique par défaut si elle n'existe pas
                if (!window.trustedTypes.defaultPolicy) {
                    const policy = window.trustedTypes.createPolicy('default', {
                        createHTML: (string) => {
                            // Pour Rails/Turbo, le contenu provient généralement de sources fiables
                            // Appliquer un nettoyage minimal pour les cas d'utilisation Rails/Devise
                            return string
                        },
                        createScript: (string) => {
                            return string
                        },
                        createScriptURL: (string) => {
                            return string
                        }
                    })

                    console.debug('✅ Politique Trusted Types créée avec succès')
                }

                // Patch spécifique pour DOMParser utilisé par Turbo
                this.patchDOMParser()

            } catch (error) {
                // Si la création de politique échoue, logger l'erreur mais ne pas bloquer
                console.warn('⚠️ Impossible de créer la politique Trusted Types:', error.message)

                // Essayer une approche alternative pour contourner le problème
                this.patchDOMParserFallback()
                this.patchInnerHTMLAssignments()
            }
        }
    }

    // Méthode de contournement pour les navigateurs qui bloquent les assignations innerHTML
    patchInnerHTMLAssignments() {
        // Cette approche n'est recommandée qu'en dernier recours
        console.warn('🔧 Application du patch de contournement pour innerHTML')

        // Sauvegarder la méthode originale
        const originalInnerHTML = Object.getOwnPropertyDescriptor(Element.prototype, 'innerHTML')

        if (originalInnerHTML) {
            Object.defineProperty(Element.prototype, 'innerHTML', {
                set: function (value) {
                    try {
                        // Essayer d'assigner normalement
                        originalInnerHTML.set.call(this, value)
                    } catch (error) {
                        if (error.message.includes('TrustedHTML')) {
                            // Si l'erreur est liée aux Trusted Types, essayer avec textContent
                            console.warn('Contournement innerHTML vers textContent pour:', this)
                            this.textContent = value
                        } else {
                            throw error
                        }
                    }
                },
                get: originalInnerHTML.get
            })
        }
    }

    // Patch pour DOMParser utilisé par Turbo avec les Trusted Types
    patchDOMParser() {
        if (typeof window.trustedTypes !== 'undefined' && window.trustedTypes.defaultPolicy) {
            const originalParseFromString = DOMParser.prototype.parseFromString

            DOMParser.prototype.parseFromString = function (source, mimeType) {
                try {
                    // Essayer avec la source originale d'abord
                    return originalParseFromString.call(this, source, mimeType)
                } catch (error) {
                    if (error.message.includes('TrustedHTML')) {
                        // Convertir en TrustedHTML avec notre politique
                        const trustedSource = window.trustedTypes.defaultPolicy.createHTML(source)
                        return originalParseFromString.call(this, trustedSource, mimeType)
                    }
                    throw error
                }
            }

            console.debug('🔧 DOMParser patché pour Trusted Types')
        }
    }

    // Fallback pour DOMParser quand les Trusted Types échouent
    patchDOMParserFallback() {
        console.warn('🆘 Application du patch de secours pour DOMParser')

        const originalParseFromString = DOMParser.prototype.parseFromString

        DOMParser.prototype.parseFromString = function (source, mimeType) {
            try {
                return originalParseFromString.call(this, source, mimeType)
            } catch (error) {
                if (error.message.includes('TrustedHTML')) {
                    console.warn('🔄 Contournement DOMParser - création manuelle du document')

                    // Créer un document vide et insérer le contenu manuellement
                    const doc = originalParseFromString.call(this, '<html><head></head><body></body></html>', mimeType)

                    // Essayer d'insérer le contenu de manière sécurisée
                    try {
                        doc.documentElement.innerHTML = source
                    } catch (innerError) {
                        // Si innerHTML échoue aussi, créer une structure basique
                        console.warn('⚠️ Fallback vers structure HTML basique')
                        doc.body.textContent = source
                    }

                    return doc
                }
                throw error
            }
        }
    }
}